import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isAppInstalled, canInstall, promptInstall } from '../lib/pwa';

const InstallAppBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was previously dismissed in this session
    const wasDismissed = sessionStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }

    // Listen for install prompt availability
    const handleInstallAvailable = () => {
      if (!isAppInstalled()) {
        setShowBanner(true);
      }
    };

    // Listen for app installation
    const handleInstalled = () => {
      setShowBanner(false);
    };

    // Check if install prompt is already available
    if (canInstall() && !isAppInstalled()) {
      setShowBanner(true);
    }

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-installed', handleInstalled);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-installed', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const accepted = await promptInstall();
      if (accepted) {
        setShowBanner(false);
      }
    } finally {
      setInstalling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner || dismissed) {
    return null;
  }

  return (
    <>
      {/* Mobile Bottom Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden animate-slide-up"
        data-testid="install-banner-mobile"
      >
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-2xl shadow-emerald-900/40 p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold text-white text-sm">
                {t('home.installBanner.title')}
              </h3>
              <p className="text-emerald-100 text-xs mt-0.5">
                {t('home.installBanner.desc')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleInstall}
                disabled={installing}
                size="sm"
                className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-4 font-medium shadow-lg"
                data-testid="install-btn-mobile"
              >
                {installing ? (
                  <span className="animate-pulse">...</span>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-1" />
                    {t('home.installBanner.install')}
                  </>
                )}
              </Button>
              <button 
                onClick={handleDismiss}
                className="p-1.5 rounded-full hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                aria-label="Dismiss"
                data-testid="dismiss-btn-mobile"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Floating Banner */}
      <div 
        className="fixed bottom-6 right-6 z-50 hidden md:block animate-slide-up"
        data-testid="install-banner-desktop"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 max-w-sm">
          <button 
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
            data-testid="dismiss-btn-desktop"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/30">
              <span className="text-white font-bold text-2xl">P</span>
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-foreground">
                {t('home.installBanner.appTitle')}
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                {t('home.installBanner.appDesc')}
              </p>
              <Button
                onClick={handleInstall}
                disabled={installing}
                className="mt-4 w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="install-btn-desktop"
              >
                {installing ? (
                  <span className="animate-pulse">{t('home.installBanner.installing')}</span>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    {t('home.installBanner.installApp')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.4s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default InstallAppBanner;
